import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/Button';
const slides = [{
  title: 'Hello, Mama',
  subtitle: "Aurora Care helps you monitor your baby's heartbeat every single day, wherever you are.",
  illustration: <svg viewBox="0 0 200 200" className="w-full h-full max-w-[280px] mx-auto" data-id="element-1244">
        <circle cx="100" cy="100" r="80" fill="#F3E8FF" data-id="element-1245" />
        <path d="M100 40 C80 40, 70 60, 70 80 C70 110, 100 140, 100 140 C100 140, 130 110, 130 80 C130 60, 120 40, 100 40 Z" fill="#6B21E8" opacity="0.8" data-id="element-1246" />
        <circle cx="100" cy="100" r="15" fill="#FFFFFF" className="animate-pulse" data-id="element-1247" />
        <path d="M95 100 L100 105 L110 95" stroke="#6B21E8" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" data-id="element-1248" />
      </svg>
}, {
  title: 'Wear & Forget',
  subtitle: 'Simply wear the Aurora Care belt and it silently monitors and records everything automatically.',
  illustration: <svg viewBox="0 0 200 200" className="w-full h-full max-w-[280px] mx-auto" data-id="element-1249">
        <circle cx="100" cy="100" r="80" fill="#F3E8FF" data-id="element-1250" />
        <rect x="40" y="85" width="120" height="30" rx="15" fill="#6B21E8" opacity="0.8" data-id="element-1251" />
        <circle cx="100" cy="100" r="20" fill="#FFFFFF" data-id="element-1252" />
        <path d="M140 100 Q150 80 160 100 T180 100" stroke="#00D4D4" strokeWidth="4" fill="none" strokeLinecap="round" className="animate-pulse" data-id="element-1253" />
        <path d="M60 100 Q50 120 40 100 T20 100" stroke="#00D4D4" strokeWidth="4" fill="none" strokeLinecap="round" className="animate-pulse" data-id="element-1254" />
      </svg>
}, {
  title: 'Always Connected',
  subtitle: 'Live data streams straight to your phone. Share reports with your doctor in seconds.',
  illustration: <svg viewBox="0 0 200 200" className="w-full h-full max-w-[280px] mx-auto" data-id="element-1255">
        <circle cx="100" cy="100" r="80" fill="#F3E8FF" data-id="element-1256" />
        <rect x="70" y="50" width="60" height="100" rx="8" fill="#FFFFFF" stroke="#E9D5FF" strokeWidth="2" data-id="element-1257" />
        <path d="M75 100 L85 100 L90 80 L100 120 L105 100 L125 100" stroke="#6B21E8" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" data-id="element-1258" />
        <circle cx="100" cy="130" r="10" fill="#00D4D4" data-id="element-1259" />
        <path d="M130 140 Q140 120 150 140" stroke="#2D2D2D" strokeWidth="3" fill="none" strokeLinecap="round" data-id="element-1260" />
        <circle cx="140" cy="115" r="8" fill="#2D2D2D" data-id="element-1261" />
      </svg>
}];
export function IntroSlides() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [direction, setDirection] = useState<'left' | 'right'>('right');
  const navigate = useNavigate();
  const touchStartX = useRef<number | null>(null);
  const nextSlide = () => {
    if (currentSlide === slides.length - 1) {
      navigate('/register');
    } else {
      setDirection('right');
      setCurrentSlide(prev => prev + 1);
    }
  };
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const touchEndX = e.changedTouches[0].clientX;
    const diff = touchStartX.current - touchEndX;
    if (diff > 50) {
      nextSlide();
    } else if (diff < -50 && currentSlide > 0) {
      setDirection('left');
      setCurrentSlide(prev => prev - 1);
    }
    touchStartX.current = null;
  };
  return <div className="min-h-screen w-full bg-background flex flex-col items-center justify-between p-6 animate-fadeInUp md:bg-secondary md:justify-center" data-id="element-1262">
      <div className="w-full max-w-[480px] h-full min-h-[100dvh] md:min-h-[600px] md:h-[80vh] bg-background md:rounded-card md:shadow-elevated flex flex-col relative overflow-hidden" data-id="element-1263">
        {/* Progress Pills */}
        <div className="flex justify-center space-x-2 pt-12 pb-8 z-10" data-id="element-1264">
          {slides.map((_, idx) => <div key={idx} className={`h-2 rounded-pill transition-all duration-300 ${idx === currentSlide ? 'w-6 bg-primary' : 'w-2 bg-border'}`} data-id="element-1265" />)}
        </div>

        {/* Slides Container */}
        <div className="flex-1 relative w-full" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd} data-id="element-1266">
          {slides.map((slide, idx) => <div key={idx} className={`absolute inset-0 flex flex-col items-center transition-transform duration-300 ease-in-out ${idx === currentSlide ? 'translate-x-0 opacity-100' : idx < currentSlide ? '-translate-x-full opacity-0' : 'translate-x-full opacity-0'}`} data-id="element-1267">
              <div className="w-full h-[40%] flex items-center justify-center mb-8" data-id="element-1268">
                {slide.illustration}
              </div>
              <div className="px-8 text-center" data-id="element-1269">
                <h2 className="text-[26px] font-semibold text-text-primary mb-3" data-id="element-1270">
                  {slide.title}
                </h2>
                <p className="text-[16px] text-text-secondary max-w-[280px] mx-auto leading-relaxed" data-id="element-1271">
                  {slide.subtitle}
                </p>
              </div>
            </div>)}
        </div>

        {/* Bottom Actions */}
        <div className="p-6 pb-12 w-full flex flex-col items-center z-10 bg-background" data-id="element-1272">
          <Button onClick={nextSlide} className="max-w-[320px]" data-id="element-1273">
            {currentSlide === slides.length - 1 ? 'Get Started' : 'Next'}
          </Button>
          <button onClick={() => navigate('/login')} className="mt-6 text-primary font-medium text-[14px] hover:opacity-80 transition-opacity" data-id="element-1274">
            Already have an account? Sign in
          </button>
        </div>
      </div>
    </div>;
}